import * as RadixDialog from "@radix-ui/react-dialog";

// The inbox's vocabulary: three verbs, each named by what it does to the ledger.
// GraphQL keeps the pipeline names (BIND / CONFIRM / DISMISS); people see these.
export const VERBS = {
  BIND: { label: "Match", consequence: "same payment as this bank or wallet row — the bank row becomes the event" },
  CONFIRM: { label: "Keep", consequence: "a real cost — count it in your books" },
  DISMISS: { label: "Exclude", consequence: "not a cost — drop it from your books" },
  UNBIND: { label: "Not the same payment", consequence: "unlink it; the record comes back as its own cost" },
  RELEASE: { label: "Undo", consequence: "back to undecided — the next sync or upload may match it automatically" },
} as const;

export const DRIFT_SENTENCE =
  "A wallet settles what QuickBooks booked minus fees and FX, so a bank row within $50 or 2% and three days of a record is the same payment.";

export const VERDICT_HINTS: Record<string, string> = {
  FLAGGED: "Inside bank statement coverage with no matching bank transaction — not counted.",
  PENDING: "Near a coverage boundary — waiting for evidence, not counted yet.",
  DEMOTED: "Bank coverage arrived without a twin — still counted until you decide.",
  RETRACTED: "Deleted in QuickBooks after entering your books — still counted until you decide.",
  SEVERED: "Its QuickBooks connection was retracted — still counted until you decide.",
};

export const COUNTED_VERDICTS = new Set(["DEMOTED", "RETRACTED", "SEVERED"]);

export const cents = (value: number) =>
  (value / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const signed = (value: number) => `${value > 0 ? "+" : ""}${cents(value)}`;

// A group or filter action above this money or count asks first (R11).
export const CONFIRM_ABOVE_CENTS = 50_000;
export const CONFIRM_ABOVE_COUNT = 100;

export type Candidate = {
  readonly txn: { readonly id: string; readonly amount: number; readonly postingDate?: string | null; readonly description?: string | null };
  readonly exact: boolean;
  readonly deltaCents: number;
  readonly payee: boolean;
  readonly rivalCounterparty?: string | null;
  readonly rivalDate?: string | null;
};

// The bind picker: exact twins first, then near twins with their delta. Unfiltered
// by payee on purpose (a person may know better than the token rule); the rows the
// automatic pass would accept are marked, and a twin another record also claims
// says so — that tie is exactly what the pass refused to break.
export function CandidatePicker({
  candidates,
  busy,
  onBind,
}: {
  candidates: ReadonlyArray<Candidate>;
  busy: boolean;
  onBind: (bankTxnId: string) => void;
}) {
  if (candidates.length === 0) {
    return <p className="text-xs text-gray-400">No unmatched bank or wallet row within three days and the amount band.</p>;
  }
  return (
    <ul className="space-y-1">
      {candidates.map((candidate) => (
        <li key={candidate.txn.id} className="flex flex-wrap items-center gap-3 text-xs text-gray-700">
          <span className="tabular-nums text-gray-500">{candidate.txn.postingDate ?? "—"}</span>
          <span className="max-w-[28rem] truncate">{candidate.txn.description ?? "—"}</span>
          <span className="tabular-nums">{cents(candidate.txn.amount)}</span>
          {candidate.exact ? (
            <span className="rounded bg-green-50 px-1.5 py-0.5 text-[11px] text-green-700">exact</span>
          ) : (
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] tabular-nums text-amber-800">
              near · Δ {signed(candidate.deltaCents)}
            </span>
          )}
          {!candidate.payee && (
            <span className="text-[11px] text-gray-400" title="The bank description shares no payee word with the QuickBooks counterparty">
              different payee
            </span>
          )}
          {candidate.rivalCounterparty != null && (
            <span className="text-[11px] text-gray-400">
              also the only candidate for {candidate.rivalCounterparty} {candidate.rivalDate ?? ""}
            </span>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => onBind(candidate.txn.id)}
            title={VERBS.BIND.consequence}
            className="rounded border border-indigo-300 px-2 py-0.5 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
          >
            {VERBS.BIND.label}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function VerbButton({
  verb,
  onClick,
  disabled,
  className = "",
}: {
  verb: "CONFIRM" | "DISMISS";
  onClick: () => void;
  disabled: boolean;
  className?: string;
}) {
  const tone =
    verb === "CONFIRM"
      ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
      : "border-red-200 text-red-700 hover:bg-red-50";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={VERBS[verb].consequence}
      className={`rounded border px-2 py-0.5 text-xs disabled:opacity-50 ${tone} ${className}`}
    >
      {VERBS[verb].label}
    </button>
  );
}

// R11 — a big action asks first, naming what moves in the ledger. Nothing is
// portalled from inside it (Radix modal + body portals do not mix).
export type Confirmation = {
  verb: "CONFIRM" | "DISMISS";
  label: string;
  count: number;
  sumCents: number;
  counted: number;
  countedSumCents: number;
  uncounted: number;
  uncountedSumCents: number;
  proceed: () => void;
};

export function ConfirmationDialog({ confirmation, onClose }: { confirmation: Confirmation | null; onClose: () => void }) {
  return (
    <RadixDialog.Root open={confirmation != null} onOpenChange={(open) => !open && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-black/30" />
        <RadixDialog.Content className="fixed inset-0 m-auto h-fit w-[90vw] max-w-[28rem] rounded-lg bg-white p-5 shadow-xl focus:outline-none">
          {confirmation && (
            <>
              <RadixDialog.Title className="text-base font-semibold text-gray-900">
                {VERBS[confirmation.verb].label} {confirmation.count.toLocaleString()} record
                {confirmation.count === 1 ? "" : "s"} for {confirmation.label || "records without a counterparty"}?
              </RadixDialog.Title>
              <RadixDialog.Description className="mt-2 text-sm text-gray-600">
                {VERBS[confirmation.verb].consequence}. Total {cents(confirmation.sumCents)}.
              </RadixDialog.Description>
              <ul className="mt-3 space-y-1 text-sm text-gray-700">
                {confirmation.verb === "DISMISS" ? (
                  <>
                    <li>
                      {confirmation.counted.toLocaleString()} currently counted — {cents(confirmation.countedSumCents)} leaves your books
                    </li>
                    <li>{confirmation.uncounted.toLocaleString()} not counted — nothing changes for them</li>
                  </>
                ) : (
                  <>
                    <li>
                      {confirmation.uncounted.toLocaleString()} not counted — {cents(confirmation.uncountedSumCents)} enters your books
                    </li>
                    <li>{confirmation.counted.toLocaleString()} already counted — they stay</li>
                  </>
                )}
              </ul>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={onClose} className="rounded border px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    confirmation.proceed();
                    onClose();
                  }}
                  className={`rounded px-3 py-1.5 text-sm text-white ${
                    confirmation.verb === "DISMISS" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {VERBS[confirmation.verb].label} all
                </button>
              </div>
            </>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
